import React, { useState } from 'react';
import { Modal, Tabs, Tag, Table, Divider } from 'antd';
import {
    InfoCircleOutlined,
    CheckCircleFilled,
    ClockCircleFilled,
    CloseCircleFilled,
    MinusCircleFilled,
    QuestionCircleFilled,
    StarFilled,
    WarningFilled,
    BulbFilled,
    SafetyCertificateFilled,
    EyeFilled,
} from '@ant-design/icons';

// ─── Shared atoms ─────────────────────────────────────────────────────────────
const Section = ({ title, icon, color, children }) => (
    <div style={{ marginBottom: 24 }}>
        <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 14px', borderRadius: 6, marginBottom: 12,
            backgroundColor: `${color}12`, borderLeft: `4px solid ${color}`,
        }}>
            <span style={{ color, fontSize: 16 }}>{icon}</span>
            <span style={{ fontWeight: 700, fontSize: 14, color }}>{title}</span>
        </div>
        <div style={{ paddingLeft: 4 }}>{children}</div>
    </div>
);

const BulletList = ({ items, color = '#555' }) => (
    <ul style={{ paddingLeft: 20, margin: 0 }}>
        {items.map((item, i) => (
            <li key={i} style={{ color, fontSize: 12, lineHeight: 1.8, marginBottom: 2 }}>{item}</li>
        ))}
    </ul>
);

const ScoreCard = ({ icon, label, color, bg, criteria, percentage, items }) => (
    <div style={{
        border: `2px solid ${color}`, borderRadius: 10, overflow: 'hidden', marginBottom: 16,
    }}>
        {/* Header */}
        <div style={{
            backgroundColor: color, padding: '10px 16px',
            display: 'flex', alignItems: 'center', gap: 10,
        }}>
            <span style={{ fontSize: 20, color: '#fff' }}>{icon}</span>
            <div>
                <div style={{ fontWeight: 800, fontSize: 14, color: '#fff' }}>{label}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', marginTop: 1 }}>{criteria}</div>
            </div>
            <div style={{ marginLeft: 'auto', backgroundColor: 'rgba(255,255,255,0.25)', padding: '3px 12px', borderRadius: 20 }}>
                <span style={{ fontWeight: 800, fontSize: 13, color: '#fff' }}>{percentage}</span>
            </div>
        </div>
        {/* Body */}
        <div style={{ backgroundColor: bg, padding: '12px 16px' }}>
            <div style={{ fontSize: 11, color: '#666', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Also applies when:
            </div>
            <BulletList items={items} />
        </div>
    </div>
);

// ─── Tab 1: Scoring Guide ─────────────────────────────────────────────────────
function ScoringGuide() {
    return (
        <div style={{ maxHeight: '65vh', overflowY: 'auto', paddingRight: 4 }}>

            <Section title="How MEs are Scored" icon={<InfoCircleOutlined />} color="#1890ff">
                <p style={{ fontSize: 12, color: '#555', lineHeight: 1.8, margin: '0 0 12px' }}>
                    Each Measurable Element (ME) in the audit tool is scored individually using one of five values below.
                    The scoring reflects the degree to which the facility's policy and/or practice meets the criterion.
                </p>
            </Section>

            <ScoreCard
                icon={<CheckCircleFilled />}
                label="MET (M)"
                color="#389e0d"
                bg="#f6ffed"
                criteria="Answer is 'Yes' or 'Always' — facility consistently meets the requirement"
                percentage="100% score"
                items={[
                    '90% or more of observations or records (e.g. 9 out of 10) are met',
                    'A single negative observation may not prevent a score of "fully met"',
                    'Evidence is of high quality and significantly exceeds the requirement',
                    'Evidence achieved through a deliberate and innovative approach',
                    'Evidence is sustainable and has significantly improved over time',
                    'Indicative of best practice',
                ]}
            />

            <ScoreCard
                icon={<ClockCircleFilled />}
                label="PARTIALLY MET (PM)"
                color="#fa8c16"
                bg="#fff7e6"
                criteria="Answer is 'Usually' or 'Sometimes' — commitment demonstrated but not fully meeting requirement"
                percentage="50% score"
                items={[
                    '50% to 89% of records/observations demonstrate compliance (e.g. 5–8 out of 10)',
                    'Previously "Not Met" at last survey, now 75%–89% observations in compliance',
                    'Compliance found in some areas/departments but not all where applicable',
                    'When multiple requirements in one ME, at least 50% are present',
                    'Policy/process is developed and implemented but lacks track record for "Met"',
                    'Late-developed policy',
                    'Policy/process exists but does not appear sustainable',
                ]}
            />

            <ScoreCard
                icon={<CloseCircleFilled />}
                label="NOT MET (NM)"
                color="#f5222d"
                bg="#fff2f0"
                criteria="Answer is 'Rarely' or 'Never' — facility does not meet the requirement"
                percentage="0% score"
                items={[
                    '49% or fewer records/observations demonstrate compliance (e.g. 4 or less out of 10)',
                    'Previously "Not Met" at last survey, now 74% or fewer observations in compliance',
                    'When multiple requirements in one ME, 49% or fewer are present',
                    'A policy/process is developed but is NOT implemented',
                ]}
            />

            <ScoreCard
                icon={<MinusCircleFilled />}
                label="NOT APPLICABLE (NA)"
                color="#595959"
                bg="#f5f5f5"
                criteria="ME requirements do not apply to this facility's scope of services or patient population"
                percentage="Excluded from score"
                items={[
                    'Hospital does not conduct research (research-related MEs)',
                    'Facility does not provide the specific service the ME references',
                    'Patient population makes the criterion irrelevant',
                    'NA items are NOT counted in the total score calculation (A is reduced)',
                ]}
            />

            <ScoreCard
                icon={<QuestionCircleFilled />}
                label="NOT CHECKED (NC)"
                color="#722ed1"
                bg="#f9f0ff"
                criteria="Criterion is relevant but current conditions prevent assessment"
                percentage="Excluded from score"
                items={[
                    'Auditor wants to assess code response, but patient has highly infectious disease',
                    'Physical access to the area is restricted for safety or security reasons',
                    'NC items are NOT counted in the total score calculation (A is reduced)',
                ]}
            />
        </div>
    );
}

// ─── Tab 2: Score Formula ─────────────────────────────────────────────────────
function ScoreFormula() {
    const formulaRows = [
        { key: 'A', label: 'A', desc: 'Total Count of Audited MEs', formula: 'A = C + D + E', note: 'NA and NC are excluded' },
        { key: 'B', label: 'B', desc: 'Score per "Met" ME', formula: 'B = 100 ÷ A', note: 'Equal weight per ME' },
        { key: 'C', label: 'C × B', desc: '% Met contribution', formula: 'C × B', note: 'Full points for Met' },
        { key: 'D', label: '(D × B) ÷ 2', desc: '% Partially Met contribution', formula: '(D × B) ÷ 2', note: 'Half points for Partial' },
        { key: 'E', label: 'E × 0', desc: '% Not Met contribution', formula: 'E × B × 0 = 0', note: 'Zero points for Not Met' },
    ];

    return (
        <div style={{ maxHeight: '65vh', overflowY: 'auto', paddingRight: 4 }}>
            <Section title="Scoring Formula" icon={<InfoCircleOutlined />} color="#1890ff">
                <div style={{ backgroundColor: '#f0f5ff', borderRadius: 8, padding: '16px 20px', marginBottom: 16, fontFamily: 'monospace', fontSize: 13, lineHeight: 2.2 }}>
                    <div><strong>A</strong> = C + D + E &nbsp;&nbsp;<span style={{ color: '#888', fontFamily: 'sans-serif', fontSize: 11 }}>(total audited MEs, excluding NA and NC)</span></div>
                    <div><strong>B</strong> = 100 ÷ A &nbsp;&nbsp;<span style={{ color: '#888', fontFamily: 'sans-serif', fontSize: 11 }}>(score per Met ME)</span></div>
                    <Divider style={{ margin: '8px 0', borderColor: '#adc6ff' }} />
                    <div><span style={{ color: '#389e0d', fontWeight: 700 }}>%Met</span> = C × B</div>
                    <div><span style={{ color: '#fa8c16', fontWeight: 700 }}>%Partially Met</span> = (D × B) ÷ 2</div>
                    <div><span style={{ color: '#f5222d', fontWeight: 700 }}>%Not Met</span> = E × B × 0 = <strong>0</strong></div>
                    <Divider style={{ margin: '8px 0', borderColor: '#adc6ff' }} />
                    <div style={{ fontSize: 14 }}><strong>Total Score</strong> = %Met + %Partially Met + %Not Met</div>
                </div>
                <div style={{ backgroundColor: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, padding: '10px 14px', fontSize: 12, color: '#614700' }}>
                    <strong>Overall Audit Score</strong> = mean value of all clause total scores
                </div>
            </Section>

            <Section title="Worked Example" icon={<BulbFilled />} color="#faad14">
                <div style={{ fontSize: 12, color: '#555', marginBottom: 10 }}>
                    Suppose a clause has <strong>10 MEs</strong> assessed:
                    <strong style={{ color: '#389e0d' }}> 6 Met</strong>,
                    <strong style={{ color: '#fa8c16' }}> 3 Partially Met</strong>,
                    <strong style={{ color: '#f5222d' }}> 1 Not Met</strong>,
                    <strong style={{ color: '#595959' }}> 0 NA/NC</strong>
                </div>
                <div style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, padding: '12px 16px', fontFamily: 'monospace', fontSize: 12, lineHeight: 2 }}>
                    <div>A = 6 + 3 + 1 = <strong>10</strong></div>
                    <div>B = 100 ÷ 10 = <strong>10</strong></div>
                    <div style={{ color: '#389e0d' }}>%Met = 6 × 10 = <strong>60</strong></div>
                    <div style={{ color: '#fa8c16' }}>%Partially Met = (3 × 10) ÷ 2 = <strong>15</strong></div>
                    <div style={{ color: '#f5222d' }}>%Not Met = 0</div>
                    <Divider style={{ margin: '4px 0', borderColor: '#b7eb8f' }} />
                    <div style={{ fontSize: 14 }}>Total Score = 60 + 15 + 0 = <strong>75%</strong></div>
                </div>
            </Section>

            <Section title="Scoring Scale" icon={<StarFilled />} color="#faad14">
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f0f0f0' }}>
                            <th style={{ padding: '7px 12px', textAlign: 'left', borderBottom: '2px solid #d9d9d9' }}>Score Range</th>
                            <th style={{ padding: '7px 12px', textAlign: 'left', borderBottom: '2px solid #d9d9d9' }}>Interpretation</th>
                            <th style={{ padding: '7px 12px', textAlign: 'left', borderBottom: '2px solid #d9d9d9' }}>Recommendation</th>
                            <th style={{ padding: '7px 12px', textAlign: 'center', borderBottom: '2px solid #d9d9d9' }}>Stars</th>
                        </tr>
                    </thead>
                    <tbody>
                        {[
                            { range: '98 – 100%', label: 'Outstanding',           rec: 'Ready for ISO accreditation',           stars: 5, color: '#389e0d' },
                            { range: '95 – 97.9%', label: 'Excellent',            rec: 'Ready for ISO accreditation',           stars: 4, color: '#1890ff' },
                            { range: '90 – 94.9%', label: 'Good',                 rec: 'Almost ready for ISO accreditation',    stars: 3, color: '#faad14' },
                            { range: '85 – 89.9%', label: 'Fair',                 rec: 'Somewhat ready for ISO accreditation',  stars: 2, color: '#fa8c16' },
                            { range: '70 – 84.9%', label: 'Requires Improvement', rec: 'Not ready for ISO accreditation',       stars: 1, color: '#f5222d' },
                            { range: '< 70%',      label: 'Inadequate',           rec: 'Cause for concern',                    stars: 0, color: '#820014' },
                        ].map((row, i) => (
                            <tr key={i} style={{ backgroundColor: i % 2 ? '#fafafa' : '#fff' }}>
                                <td style={{ padding: '7px 12px', fontFamily: 'monospace', fontWeight: 700, color: row.color, borderBottom: '1px solid #f0f0f0' }}>{row.range}</td>
                                <td style={{ padding: '7px 12px', fontWeight: 700, color: row.color, borderBottom: '1px solid #f0f0f0' }}>{row.label}</td>
                                <td style={{ padding: '7px 12px', color: '#555', borderBottom: '1px solid #f0f0f0', fontSize: 11 }}>{row.rec}</td>
                                <td style={{ padding: '7px 12px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>
                                    {[1,2,3,4,5].map(s => (
                                        <StarFilled key={s} style={{ color: s <= row.stars ? '#faad14' : '#e8e8e8', fontSize: 12 }} />
                                    ))}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Section>
        </div>
    );
}

// ─── Tab 3: Key Critical Standards ───────────────────────────────────────────
function KCSGuide() {
    return (
        <div style={{ maxHeight: '65vh', overflowY: 'auto', paddingRight: 4 }}>
            <Section title="Key Critical Standards (KCS)" icon={<SafetyCertificateFilled />} color="#f5222d">
                <div style={{ backgroundColor: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 12, color: '#5c0011', lineHeight: 1.8 }}>
                    <strong>Definition:</strong> KCS are highly critical standards regarding the quality of care level provided
                    by a healthcare facility. In case they are not met, they can have extremely fatal consequences for a patient.
                    MEs related to KCS are marked <span style={{ color: '#f5222d', fontWeight: 700 }}>red</span> in the audit tool
                    and should be treated as <strong>"MUST BE MET"</strong> by the facility.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {[
                        { n: 1, title: 'Define patients correctly', desc: 'Correct patient identification before every procedure, medication, or treatment.' },
                        { n: 2, title: 'Improve communication', desc: 'Accurate, complete, and timely communication between care team members.' },
                        { n: 3, title: 'Ensure safety of high-alert medications', desc: 'Safe handling, storage, and administration of high-risk medications.' },
                        { n: 4, title: 'Implement safe surgery', desc: 'Surgical safety checklist, correct site, correct procedure, correct patient.' },
                        { n: 5, title: 'Minimize risk of healthcare-associated infections', desc: 'IPC protocols consistently applied to prevent HAIs.' },
                        { n: 6, title: 'Minimize risk of patient harm from falls', desc: 'Fall prevention protocols identified, assessed, and applied.' },
                    ].map(kcs => (
                        <div key={kcs.n} style={{ border: '2px solid #ffccc7', borderRadius: 8, padding: '12px 14px', backgroundColor: '#fff' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                <div style={{
                                    minWidth: 28, height: 28, borderRadius: '50%', backgroundColor: '#f5222d',
                                    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontWeight: 800, fontSize: 13, flexShrink: 0,
                                }}>{kcs.n}</div>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 12, color: '#f5222d', marginBottom: 4 }}>{kcs.title}</div>
                                    <div style={{ fontSize: 11, color: '#666', lineHeight: 1.6 }}>{kcs.desc}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Section>
        </div>
    );
}

// ─── Tab 4: Comments & Observations ──────────────────────────────────────────
function ObservationsGuide() {
    return (
        <div style={{ maxHeight: '65vh', overflowY: 'auto', paddingRight: 4 }}>
            <Section title="Comments" icon={<InfoCircleOutlined />} color="#1890ff">
                <p style={{ fontSize: 12, color: '#555', lineHeight: 1.8 }}>
                    A comment is a written note to explain or interpret the assessment of an ME.
                    The auditor <strong>should always</strong> add a comment and <strong>MUST</strong> add a comment if an ME is
                    scored anything less than "Met", or is considered Not Applicable or Not Checked.
                </p>
            </Section>

            <Section title="Observations" icon={<EyeFilled />} color="#722ed1">
                <p style={{ fontSize: 12, color: '#555', lineHeight: 1.8 }}>
                    Observations are specific evidence and observed practices gathered by the auditor that are
                    <strong> not directly included</strong> in the audit standards. If an observation is made,
                    its criticality must subsequently be rated.
                </p>
                <div style={{ backgroundColor: '#f9f0ff', border: '1px solid #d3adf7', borderRadius: 6, padding: '10px 14px', fontSize: 12, color: '#391085', fontStyle: 'italic' }}>
                    Example: While assessing medication storage, the auditor observes a water leakage.
                    Although not directly related to the standard, it should be recorded and rated.
                </div>
            </Section>

            <Section title="Criticality of Observation" icon={<WarningFilled />} color="#fa8c16">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[
                        { level: 'High Risk', color: '#f5222d', bg: '#fff2f0', border: '#ffccc7', desc: 'A major observation that can seriously compromise patient and staff safety and may result in huge reputational and economic damage.' },
                        { level: 'Medium Risk', color: '#fa8c16', bg: '#fff7e6', border: '#ffd591', desc: 'A serious observation that may impact patient and staff safety but not as immediate as a high risk observation.' },
                        { level: 'Low Risk', color: '#389e0d', bg: '#f6ffed', border: '#b7eb8f', desc: 'A minor observation with little to no impact to patient and staff safety and may not compromise the activities and reputation of the facility.' },
                        { level: 'Not Applicable', color: '#595959', bg: '#f5f5f5', border: '#d9d9d9', desc: 'No additional observation from the auditor on the standard being measured.' },
                    ].map(r => (
                        <div key={r.level} style={{ border: `1px solid ${r.border}`, borderRadius: 8, overflow: 'hidden', display: 'flex' }}>
                            <div style={{ backgroundColor: r.color, width: 6, flexShrink: 0 }} />
                            <div style={{ backgroundColor: r.bg, padding: '10px 14px', flex: 1 }}>
                                <div style={{ fontWeight: 700, fontSize: 12, color: r.color, marginBottom: 4 }}>{r.level}</div>
                                <div style={{ fontSize: 11, color: '#555', lineHeight: 1.6 }}>{r.desc}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            <Section title="Area of Good Practice" icon={<BulbFilled />} color="#52c41a">
                <p style={{ fontSize: 12, color: '#555', lineHeight: 1.8 }}>
                    An "Area of Good Practice" allows the auditor to take notes on something observed
                    <strong> positively</strong> during the audit, regardless of whether it is directly related to the standard.
                    This may be a method or technique that has become a standard way of doing things — e.g., a standard
                    way of complying with legal or ethical requirements.
                </p>
            </Section>
        </div>
    );
}

// ─── Main exported component ──────────────────────────────────────────────────
export function InstructionsButton({ style }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <span
                onClick={() => setOpen(true)}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    cursor: 'pointer', color: '#1890ff', fontSize: 12,
                    padding: '3px 10px', borderRadius: 20,
                    border: '1px solid #91caff', backgroundColor: '#e6f4ff',
                    fontWeight: 600, transition: 'all 0.2s',
                    ...style
                }}
                title="View audit instructions"
            >
                <InfoCircleOutlined /> Instructions
            </span>
            <InstructionsModal open={open} onClose={() => setOpen(false)} />
        </>
    );
}

export function InstructionsModal({ open, onClose }) {
    const [activeTab, setActiveTab] = useState('scoring');

    const TABS = [
        { key: 'scoring', label: 'Scoring Guide',           icon: <CheckCircleFilled style={{ color: '#389e0d' }} />,       content: <ScoringGuide /> },
        { key: 'formula', label: 'Formula & Scale',         icon: <StarFilled style={{ color: '#faad14' }} />,               content: <ScoreFormula /> },
        { key: 'kcs',     label: 'Key Critical Standards',  icon: <SafetyCertificateFilled style={{ color: '#f5222d' }} />,  content: <KCSGuide /> },
        { key: 'obs',     label: 'Comments & Observations', icon: <EyeFilled style={{ color: '#722ed1' }} />,                content: <ObservationsGuide /> },
    ];

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={780}
            style={{ top: 20 }}
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <InfoCircleOutlined style={{ color: '#1890ff', fontSize: 18 }} />
                    <span style={{ fontWeight: 700 }}>Audit Tool Instructions</span>
                    <span style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>— Eswatini QoC Standards</span>
                </div>
            }
            destroyOnClose
        >
            {/* Custom tab bar — avoids Ant Design 4 items API switching bug */}
            <div style={{
                display: 'flex', borderBottom: '2px solid #f0f0f0',
                marginBottom: 20, overflowX: 'auto', flexShrink: 0,
            }}>
                {TABS.map(tab => (
                    <div
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '10px 16px', cursor: 'pointer', whiteSpace: 'nowrap',
                            borderBottom: activeTab === tab.key ? '2px solid #1890ff' : '2px solid transparent',
                            marginBottom: -2,
                            fontWeight: activeTab === tab.key ? 700 : 400,
                            color: activeTab === tab.key ? '#1890ff' : '#555',
                            fontSize: 13,
                            transition: 'all 0.15s',
                            userSelect: 'none',
                        }}
                    >
                        {tab.icon}
                        {tab.label}
                    </div>
                ))}
            </div>

            {/* Tab content */}
            {TABS.map(tab => (
                <div key={tab.key} style={{ display: activeTab === tab.key ? 'block' : 'none' }}>
                    {tab.content}
                </div>
            ))}
        </Modal>
    );
}

export default InstructionsModal;
